import type { FC, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Breadcrumbs, AppBar, Toolbar, Box, useTheme, type Theme, Chip, styled } from '@mui/material'
import { tss } from '@dhl/theme'
import { ChevronRight } from '@dhl/icons'
import { Logo } from '@dhl/logo'

type MakeStylesProps = {
  logoWidth: number
  logoHeight: number
  logoColor?: string
  drawerWidth: number
  isMobile: boolean
  showAcceptHeader: boolean
  isAccept: boolean
  headerBackground?: string
}

const AcceptChip = styled(Chip)(() => ({
  borderRadius: 3,
}))

const getHeaderBackgroundCss = ({
  theme,
  headerBackground,
  showAcceptHeader,
  isAccept,
}: {
  theme: Theme
  headerBackground?: string
  showAcceptHeader?: boolean
  isAccept?: boolean
}) => {
  if (headerBackground) {
    return headerBackground
  }
  if (showAcceptHeader && isAccept) {
    return theme.palette.grey[300]
  }
  return undefined
}

const useStyles = tss.makeStyles<MakeStylesProps>()(
  (theme, { logoWidth, logoHeight, drawerWidth, isMobile, headerBackground, showAcceptHeader, isAccept }) => ({
    root: {
      border: 0,
      background: getHeaderBackgroundCss({ theme, headerBackground, showAcceptHeader, isAccept }),
      boxShadow: 'none',
    },
    logoContainer: {
      width: isMobile ? undefined : drawerWidth,
      marginRight: isMobile ? theme.spacing(1) : undefined,
      display: 'flex',
      alignItems: 'center',
      '& > a': {
        display: 'inline-block',
        width: logoWidth,
        height: logoHeight,
      },
    },
    headerInformation: {
      alignItems: 'center',
      background: theme.palette.info.main,
      boxShadow: 'inset 0 1px 5px -1px rgb(0, 0, 0, .2)',
      display: 'flex',
      height: theme.spacing(4),
      paddingLeft: theme.spacing(1),
    },
    chip: {
      '& span': {
        fontSize: '14px',
      },
    },
  })
)

type Props = {
  className?: string
  breadcrumbs?: ReactNode[]
  drawerWidth: number
  headerContent?: ReactNode
  headerExtraToolBar?: ReactNode
  headerBackground?: string
  headerInformation?: ReactNode
  logoColor?: string
  logoHref?: string
  position?: 'fixed' | 'absolute' | 'sticky' | 'static' | 'relative'
  withoutRouter?: boolean

  /**
   * Is the application running on acceptance or not.
   * We mostly deploy to ACC and PRD (also a preview one but edge case), and want to visually show this to our enduser.
   *
   * @default: checks wheter there is `accept` in your hostname, this is a client-side solution so
   * best is to pass your own. Some apps give an env `ENVIRONMENT` in their k8s, which is the most reliable one.
   */
  isAccept?: () => boolean

  /**
   * When you pass the isAccept function but still do not want the chip to tell so because of avilable space (mobile app)
   * you can overrule this. Make sure to then set `showAcceptHeader` so we still have some visible recognition (grey header)
   *
   * @default true
   */
  showAcceptChip?: boolean

  /**
   * When you pass the isAccept function but want to show this only by another header color turn this on
   *
   * @default false
   */
  showAcceptHeader?: boolean

  /**
   * You can have influence on the i18n text in the acceptance chip when its on
   * @default 'Acceptance'
   */
  acceptLabel?: string
}

export const AppHeader: FC<Props> = ({
  className,
  breadcrumbs,
  drawerWidth,
  logoColor,
  logoHref = '/',
  headerContent,
  headerExtraToolBar,
  headerInformation,
  headerBackground,
  position = 'sticky',
  withoutRouter = false,
  isAccept = () => (typeof window !== 'undefined' ? window.location.hostname.includes('accept') : false),
  showAcceptChip = true,
  showAcceptHeader = false,
  acceptLabel = 'Acceptance',
}) => {
  const theme = useTheme()
  const logoWidth = 112
  const logoHeight = 24
  const isOnAccept = isAccept()

  const { isMobile } = theme.device

  const { classes, cx } = useStyles({
    logoWidth,
    logoHeight,
    logoColor,
    drawerWidth,
    isMobile,
    showAcceptHeader,
    isAccept: isOnAccept,
    headerBackground,
  })

  return (
    <AppBar className={cx(classes.root, className)} position={position}>
      <Toolbar variant="dense">
        <div className={classes.logoContainer}>
          {withoutRouter && <Logo width={logoWidth} height={logoHeight} fill={logoColor} />}
          {!withoutRouter && (
            <RouterLink to={logoHref} tabIndex={-1}>
              <Logo width={logoWidth} height={logoHeight} fill={logoColor} />
            </RouterLink>
          )}
        </div>

        <Box display="flex" gap={theme.spacing(1)} alignItems="center">
          {isOnAccept && showAcceptChip && <AcceptChip color="success" label={acceptLabel} className={classes.chip} />}

          {breadcrumbs && !isMobile && (
            <Box mr={2}>
              <Breadcrumbs separator={<ChevronRight fontSize="small" />}>{breadcrumbs}</Breadcrumbs>
            </Box>
          )}
        </Box>

        <Box
          display="flex"
          flex={1}
          justifyContent="flex-end"
          alignItems="center"
          style={{ columnGap: theme.spacing(1) }}
        >
          {headerContent}
        </Box>
      </Toolbar>
      {headerExtraToolBar}
      {headerInformation && <div className={classes.headerInformation}>{headerInformation}</div>}
    </AppBar>
  )
}
